import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../utils/utils";

export const ENDPOINT_LOGS_PER_PAGE = 7;

export function getPageNumbers(currentPage: number, totalPages: number) {
  const pages = [...new Set([
    1,
    totalPages,
    ...Array.from({ length: 5 }, (_, index) => currentPage - 2 + index),
  ])].filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);

  return pages.reduce<(number | string)[]>((range, page, index) => {
    const previous = pages[index - 1];
    if (previous && page - previous === 2) {
      range.push(previous + 1);
    } else if (previous && page - previous > 2) {
      range.push("…");
    }
    range.push(page);
    return range;
  }, []);
}

interface CardPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  hasNextPage?: boolean;
  disabled?: boolean;
  className?: string;
  label?: string;
}

export default function CardPagination({
  currentPage,
  totalPages,
  onPageChange,
  hasNextPage = currentPage < totalPages,
  disabled = false,
  className,
  label = "Pagination",
}: CardPaginationProps) {
  return (
    <nav aria-label={label} className={cn("flex items-center flex-wrap gap-2", className)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={disabled || currentPage === 1}
        aria-label="Previous page"
        className="h-8 p-2 rounded-lg"
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      {getPageNumbers(currentPage, totalPages).map((page, index) =>
        typeof page === "number" ? (
          <Button
            key={page}
            type="button"
            variant={currentPage === page ? "default" : "outline"}
            size="sm"
            onClick={() => onPageChange(page)}
            disabled={disabled}
            aria-label={`Page ${page}`}
            aria-current={currentPage === page ? "page" : undefined}
            className="h-8 min-w-8 rounded-lg"
          >
            {page}
          </Button>
        ) : (
          <span key={`ellipsis-${index}`} className="px-2 select-none text-muted-foreground" aria-hidden="true">
            {page}
          </span>
        )
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={disabled || !hasNextPage}
        aria-label="Next page"
        className="h-8 p-2 rounded-lg"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </nav>
  );
}
